import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import { UserSmsThreadCollection } from '../../MedicareProAPI/Collections/User/UserSmsThreadCollection';
import type { Ind } from '../../MedicareProAPI/Entities/Ind/Ind';
import type { IndPhone } from '../../MedicareProAPI/Entities/Ind/IndPhone';
import type { User } from '../../MedicareProAPI/Entities/User/User';
import type { UserLetter } from '../../MedicareProAPI/Entities/User/UserLetter';
import { UserSmsThread } from '../../MedicareProAPI/Entities/User/UserSmsThread';
import type { UserVoipExtension } from '../../MedicareProAPI/Entities/User/UserVoipExtension';
import type { UserVoipPhoneNumber } from '../../MedicareProAPI/Entities/User/UserVoipPhoneNumber';
import { IndPhoneRepository } from '../../MedicareProAPI/Repositories/Ind/IndPhoneRepository';
import type { UserIntegrationFeatureAvailabilityResponse } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserIntegrationOAuthVoipResourceRepository } from '../../MedicareProAPI/Repositories/User/UserIntegrationOAuthVoipResourceRepository';
import { UserLetterRepository } from '../../MedicareProAPI/Repositories/User/UserLetterRepository';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import { UserSmsThreadRepository } from '../../MedicareProAPI/Repositories/User/UserSmsThreadRepository';
import { UserVoipExtensionRepository } from '../../MedicareProAPI/Repositories/User/UserVoipExtensionRepository';
import { UserVoipPhoneNumberRepository } from '../../MedicareProAPI/Repositories/User/UserVoipPhoneNumberRepository';
import { RichTextEditorLight } from './Form/Field/RichTextEditorLight';
import { IndRepository } from '../../MedicareProAPI/Repositories/Ind/IndRepository';

// Props interface for SMSChat
export interface SMSChatProps {
  props?: any;
  userAccessToken: string;
  individual: Ind;
  user: User;
}

/**
 * Component for SMSChat.
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2024-03-24
 * @lastUpdated
 * @version 1.1.0
 */
export const SMSChat = ({
  props,
  userAccessToken,
  individual,
  user,
}: SMSChatProps): React.JSX.Element => {
  const userSmsThreadRepository = new UserSmsThreadRepository();
  userSmsThreadRepository.setAccessToken(userAccessToken);

  const individualRepository = new IndRepository();
  individualRepository.setAccessToken(userAccessToken);

  const individualPhoneRepository = new IndPhoneRepository();
  individualPhoneRepository.setAccessToken(userAccessToken);

  const userLetterRepository = new UserLetterRepository();
  userLetterRepository.setAccessToken(userAccessToken);

  const userIntegrationOAuthVoipResourceRepository =
    new UserIntegrationOAuthVoipResourceRepository();
  userIntegrationOAuthVoipResourceRepository.setAccessToken(userAccessToken);

  const userVoipPhoneNumberRepository = new UserVoipPhoneNumberRepository();
  userVoipPhoneNumberRepository.setAccessToken(userAccessToken);

  const userVoipExtensionRepository = new UserVoipExtensionRepository();
  userVoipExtensionRepository.setAccessToken(userAccessToken);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(userAccessToken);

  const [individualPhones, setIndPhones] = React.useState<Array<IndPhone>>([]);
  const [userSmsThreads, setUserSmsThreads] = React.useState<Array<UserSmsThread>>([]);
  const newestUserSmsThreadRef = React.useRef<UserSmsThread | null>(null);

  const [selectedProvider, setSelectedProvider] =
    React.useState<UserIntegrationFeatureAvailabilityResponse>();
  const [availableProviders, setAvailableProviders] = React.useState<
    Array<UserIntegrationFeatureAvailabilityResponse>
  >([]);
  const [availableSendingNumbers, setAvailableSendingNumbers] = React.useState<
    Array<UserVoipPhoneNumber>
  >([]);
  const [availableSendingExtensions, setAvailableSendingExtensions] = React.useState<
    Array<UserVoipExtension>
  >([]);

  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isPrepending, setIsPrepending] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState<boolean>(true);

  const [offset, setOffset] = React.useState<number>();
  const [total, setTotal] = React.useState<number>();
  const limit = 5;

  const [fetchingProviders, setFetchingProviders] = React.useState<boolean>(true);
  const [fetchingPhoneNumbersAndExtensions, setFetchingPhoneNumbersAndExtensions] =
    React.useState<boolean>(false);
  const [needsToAddPhoneNumberHere, setNeedsToAddPhoneNumberHere] = React.useState<boolean>(false);
  const [needsToAddPhoneNumberFromSettings, setNeedsToAddPhoneNumberFromSettings] =
    React.useState<boolean>(false);
  const [newPhoneNumberIsSaving, setNewPhoneNumberIsSaving] = React.useState<boolean>(false);
  const [newPhoneNumberIsSaved, setNewPhoneNumberIsSaved] = React.useState<boolean>(false);
  const [errorSavingNewPhone, setErrorSavingNewPhone] = React.useState<boolean>(false);

  const [message, setMessage] = React.useState<string>('');
  const [sendingTo, setSendingTo] = React.useState<string>();
  const [selectedSendingPhoneNumber, setSelectedSendingPhoneNumber] = React.useState<string>();
  const [selectedSendingExtension, setSelectedSendingExtension] = React.useState<string>();
  const needsPhoneNumbers = ['dialpad', 'intulse_external', 'phonecom', 'ringcentral', 'openphone'];
  const needsExtensions = ['phonecom', 'ringcentral'];
  const canAddPhoneNumberHere = ['dialpad', 'intulse_external', 'openphone'];
  const canAddPhoneNumberFromSettings = ['phonecom', 'ringcentral'];
  const cndImageUrl = 'https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/';

  const [isSendingNewMessage, setIsSendingNewMessage] = React.useState<boolean>(false);
  const [sendingMessageError, setSendingMethodError] = React.useState<string>(null);

  const [errorMessages, setErrorMessages] = React.useState<Array<string>>([]);

  const [newMessageAlert, setNewMessageAlert] = React.useState<boolean>(false);

  const [userTemplates, setUserTemplates] = React.useState<Array<UserLetter>>(null);

  const [templateSelected, setTemplateSelected] = React.useState<number>();
  const [messageLive, setMessageLive] = React.useState<string>('');
  const [templateName, setTemplateName] = React.useState<string>('Templates');

  /**
   * Handles sending the SMS
   * @param {string} to
   * @param {string} from
   * @param {string} message
   * @param {string} accountId
   * @param {string} extensionId
   * @returns {Promise<any>}
   */
  const handleSmsSend = (
    providerReference,
    providerEntityType,
    to,
    from,
    message,
    accountId,
    extensionId
  ) => {
    if (!isValidToSend()) return;
    setIsSendingNewMessage(true);
    setSendingMethodError(null);
    // Because we don't store the sending data until a webhook is triggered on the provider, we need to temporarily display the message as part of the <collection> </collection>
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const localDateTime = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(
      now.getHours()
    )}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    const newMessage = new UserSmsThread();
    newMessage.setId(`${Date.now()}-${Math.floor(Math.random() * 1000000)}`);
    newMessage.setSmsProvider(providerReference);
    newMessage.setPhoneNumber(to);
    newMessage.setDirection('S');
    newMessage.setMessage(message);
    newMessage.setReadStatus('R');
    newMessage.setThreadDate({ date: localDateTime });
    // newMessage.setError(null);

    setUserSmsThreads([...userSmsThreads, newMessage]);

    const payload = {
      to: to,
      from: from,
      message: messageLive,
      accountId: accountId,
      extensionId: extensionId,
    };
    setMessage(message);

    webAPI
      .sendUserVoipSms(providerReference, payload, providerEntityType)
      .then((response) => {
        setMessageLive('');
        setMessage('');
        setIsSendingNewMessage(false);
        if (response && (typeof response.id === 'undefined' || response.id === '')) {
          if (response.response.message || response.response.detail) {
            const error = response.response.message || response.response.detail;
            setSendingMethodError('Unable to send SMS: ' + error);
            setErrorMessages((prev) => [...prev, newMessage.getId()]);
          }
        } else {
          setSendingMethodError(null);
        }
      })
      .catch((e) => {
        setIsSendingNewMessage(false);
        setSendingMethodError('Unable to send SMS');
      });
  };

  /**
   * Handles fetching more messages and scrolling logic
   * @param offset
   */
  const fetchPreviousMessages = async (offset: number) => {
    const container = containerRef.current;
    if (!container) return;

    if (!total || offset >= total) return;

    const previousScrollHeight = container.scrollHeight;
    setIsPrepending(true);

    const newMessages = await fetchMessages(offset);

    // Prepend messages
    setUserSmsThreads((prev) => [...newMessages.getItems(), ...prev]);
    setOffset(offset);

    // Wait for the DOM to update after messages are prepended
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!container) return;
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - previousScrollHeight;
        setIsPrepending(false);
      });
    });
  };

  /**
   * Handles API call to get sms threads
   * @param offset
   */
  const fetchMessages = async (offset: number) => {
    const messages = await userSmsThreadRepository.getUserIndSmsThread(
      user.getUserId(),
      individual.getIndId(),
      { order: 'DESC', sortBy: 'threadDate', limit: limit, offset: offset }
    );
    setIsFetching(false);
    return messages;
  };

  const getMessages = async (offset: number, cycle: number | null) => {
    const fetchedMessages = await fetchMessages(offset);
    const fetched = fetchedMessages.getItems();

    const latestFetched = fetched.length > 0 ? fetched[fetched.length - 1] : null;
    const currentNewest = newestUserSmsThreadRef.current;

    let shouldUpdate = false;

    if (!currentNewest && latestFetched) {
      shouldUpdate = true;
    } else if (currentNewest && latestFetched && latestFetched.getId() !== currentNewest.getId()) {
      shouldUpdate = true;
    }

    // This needs to store the newest payload in temporary state if the bottom of the window is not in view (the scrolled up)
    // If there is updated messages, display "new message" button
    // On the new message button click, load the newest state into the current state and rerender
    if (shouldUpdate) {
      newestUserSmsThreadRef.current = latestFetched;
      setUserSmsThreads(fetched);
      setOffset(0);
      setTotal(fetchedMessages.getTotal());
      setIsFetching(false);
      if (cycle == 1) {
        scrollToBottom();
      } else {
        setNewMessageAlert(true);
      }
    }
  };

  const scrollToBottom = () => {
    setNewMessageAlert(false);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, 0);
  };

  /**
   * Handles grouping the collection of SMS threads by date
   * @param messages
   */
  const groupMessagesByDate = (messages: UserSmsThread[]) => {
    const groups: { [date: string]: UserSmsThread[] } = {};

    messages.forEach((msg) => {
      const date = new Date(msg.getThreadDate().date).toDateString(); // "Mon Mar 18 2024"
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });

    return groups;
  };

  /**
   * Handles formatting the date in readable format for grouped messages
   * @param dateStr
   */
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  };

  /**
   * Handles formatting the time of the message
   * @param threadDate
   */
  const formatTime = (threadDate: string): string => {
    const date = new Date(threadDate);
    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  /**
   * Handles validating if the sms is sendable
   */
  const isValidToSend = () => {
    let valid = true;

    if (selectedProvider) {
      if (needsPhoneNumbers.includes(selectedProvider.reference) && !selectedSendingPhoneNumber) {
        valid = false;
        console.log('needs a sending from phone number');
      }

      if (needsExtensions.includes(selectedProvider.reference) && !selectedSendingExtension) {
        valid = false;
        console.log('needs a sending from extension');
      }

      if (needsToAddPhoneNumberHere) {
        valid = false;
        console.log('needs to add a sending phone number');
      }

      if (needsToAddPhoneNumberFromSettings) {
        valid = false;
        console.log('needs to go to settings and add a phone number');
      }

      if (!sendingTo) {
        valid = false;
        console.log('needs a phone number to send to');
      }

      if (!messageLive) {
        valid = false;
        console.log('needs a message');
      }
    }
    return valid;
    // props.isValid(valid);
  };

  const handleProviderSelect = (index) => {
    if (index == 'default') {
      setSelectedProvider(null);
    } else {
      const _selectedProvider = availableProviders[index];
      setSelectedProvider(_selectedProvider);
    }
  };

  /**
   * Handles typing in new phone number
   *
   * @param {React.SyntheticEvent} event New phone number to be saved to sms vendor
   */
  const handleNewPhoneNumberChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;
    const value = inputElement.value;

    setSelectedSendingPhoneNumber(value);
  };

  /**
   * Handles adding a user voip phone number if vendor needs it
   */
  const handlePhoneNumberSave = () => {
    setNewPhoneNumberIsSaving(true);
    const payload = {
      phoneNumber: selectedSendingPhoneNumber,
    };

    if (selectedProvider.entityType == 'oauth') {
      payload['provider'] = selectedProvider['id'];
    } else if (selectedProvider.entityType == 'pat') {
      payload['app'] = selectedProvider['id'];
    }

    userVoipPhoneNumberRepository
      .createUserVoipPhoneNumbers(user.getUserId(), payload)
      .then((response) => {
        setNewPhoneNumberIsSaving(false);
        setNewPhoneNumberIsSaved(true);
        setErrorSavingNewPhone(false);
        setNeedsToAddPhoneNumberHere(false);
      })
      .catch((e) => {
        setErrorSavingNewPhone(true);
        setNewPhoneNumberIsSaved(false);
        setNewPhoneNumberIsSaving(false);
      });
  };

  const handleTemplateSelected = (templateIndex: number) => {
    //setMessage('<i class="far fa-spinner fa-spin"></i>');
    const message = userTemplates[templateIndex];
    setTemplateName(message.getLetterTitle());
    //individualRepository.getInd(individual.getId(),{include : ["addresses"]}).then(response => {
    const newMessage = message.renderLetterBody(
      individual,
      user,
      user.getSignature(),
      user.getSignatureEmail(),
      user.getSignaturePic(),
      null,
      false,
      'sms',
      'single',
      null
    );
    setMessage(newMessage);
    setMessageLive(newMessage);
    // })
  };

  const handleSmsChange = (message) => {
    setMessageLive(message);
  };

  /*
   * Handles scrolling chat window to the bottom if UserSmsThreads is set
   */
  React.useEffect(() => {
    if (isPrepending) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [userSmsThreads]); // Smooth on new messages

  /*
   * Handles scrolling up in chat window and fetching older messages
   */
  React.useEffect(() => {
    console.log('scrolling');
    const container = containerRef.current;

    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop === 0) {
        fetchPreviousMessages(offset + limit);
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [offset, total]);

  /**
   * Handles fetching connected sms vendor phone numbers and extensions
   */
  React.useEffect(() => {
    if (selectedProvider) {
      setNeedsToAddPhoneNumberFromSettings(false);
      setAvailableSendingExtensions([]);
      setSelectedSendingExtension(null);
      setNeedsToAddPhoneNumberFromSettings(false);
      setNeedsToAddPhoneNumberHere(false);
      setSelectedSendingPhoneNumber(null);
      setAvailableSendingNumbers([]);

      // Phone numbers
      if (needsPhoneNumbers.includes(selectedProvider.reference)) {
        setFetchingPhoneNumbersAndExtensions(true);

        let fetchPhones;

        if (selectedProvider.entityType == 'oauth') {
          fetchPhones = userVoipPhoneNumberRepository.getUserVoipPhoneNumbers(
            user.getUserId(),
            selectedProvider.id
          );
        } else if (selectedProvider.entityType == 'pat') {
          fetchPhones = userVoipPhoneNumberRepository.getUserVoipPhoneNumbersApp(
            user.getUserId(),
            selectedProvider.id
          );
        }

        fetchPhones.then((phoneNumbers) => {
          setFetchingPhoneNumbersAndExtensions(false);
          if (phoneNumbers.getItems().length) {
            setNeedsToAddPhoneNumberHere(false);
            setNeedsToAddPhoneNumberFromSettings(false);
            setAvailableSendingNumbers(phoneNumbers.getItems());

            setSelectedSendingPhoneNumber(phoneNumbers.getItems()[0].phoneNumber);
          } else {
            if (canAddPhoneNumberHere.includes(selectedProvider.reference)) {
              setNeedsToAddPhoneNumberHere(true);
            } else {
              if (canAddPhoneNumberFromSettings.includes(selectedProvider.reference)) {
                setNeedsToAddPhoneNumberFromSettings(true);
              }
            }
          }
        });
      }

      // Extensions
      if (needsExtensions.includes(selectedProvider.reference)) {
        userVoipExtensionRepository
          .getUserVoipExtensions(user.getUserId(), selectedProvider.id)
          .then((extensions) => {
            if (extensions.getItems().length) {
              setAvailableSendingExtensions(extensions.getItems());

              setSelectedSendingExtension(extensions.getItems()[0].getExtensionId());
            } else {
              if (canAddPhoneNumberFromSettings.includes(selectedProvider.reference)) {
                setNeedsToAddPhoneNumberFromSettings(true);
              }
            }
          });
      }
    }
  }, [selectedProvider]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      getMessages(0, null);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  /*
   * Handlers getting initial data to interact with SMS
   */
  React.useEffect(() => {
    console.log(individual);
    // get messages
    getMessages(0, 1);

    // get individual phones
    individualPhoneRepository.getIndPhones(individual.getIndId()).then((individualPhones) => {
      setIndPhones(individualPhones.getItems());
      if (individualPhones.getItems().length > 0) {
        setSendingTo(individualPhones.getItems()[0].getIndPhoneNumber());
      }
    });

    // get user SMS provider
    userIntegrationOAuthVoipResourceRepository
      .getUserIntegrationFeatureAvailability(user.getUserId(), 'sms')
      .then((providers) => {
        if (providers.length > 0) {
          console.log(providers);
          setSelectedProvider(providers[0]);
          setAvailableProviders(providers);
        }

        setFetchingProviders(false);
      });

    // Get user templates
    userLetterRepository.getUserLetters(user.getUserId()).then((response) => {
      setUserTemplates(
        response.getItems().filter((item) => {
          return item.getLetterType() == 'SMS';
        })
      );
    });
  }, [individual]);

  return (
    <>
      <div
        className="flex-grow-1 overflow-y-auto mb-3"
        ref={containerRef}
        style={{
          maxHeight: '450px',
          overflowY: 'auto',
        }}
      >
        {isPrepending && (
          <div className="text-center" style={{ height: '25px' }}>
            <i className="fas fa-spinner fa-spin"></i>
          </div>
        )}
        {
          !isPrepending && <div className="text-center" style={{ height: '25px' }}></div> // prevents dip
        }

        {userSmsThreads &&
          !isFetching &&
          Object.entries(groupMessagesByDate(userSmsThreads))
            .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
            .map(([date, msgs], groupIdx, groupedArray) => {
              const isLastGroup = groupIdx === groupedArray.length - 1;
              const lastMsgIdx = msgs.length - 1;

              return (
                <div key={date}>
                  <div className="text-center my-3">
                    <small>{formatDate(date)}</small>
                  </div>
                  {msgs.map((msg, idx) => {
                    const isLastMsg =
                      isLastGroup && idx === lastMsgIdx && msg.getDirection() === 'S';

                    const isSent = msg.getDirection() === 'S';

                    return (
                      <React.Fragment key={msg.getThreadDate().date + idx}>
                        <div
                          className={`d-flex mb-1 ${isSent ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                          {!isSent && (
                            <div>
                              <img
                                style={{ height: '20px' }}
                                className="pt-0 mt-0"
                                src={
                                  cndImageUrl +
                                  msg.getSmsProvider().toLowerCase().replace('.', '') +
                                  'icon.png'
                                }
                              ></img>
                            </div>
                          )}

                          <div
                            className={`p-2 message ${isSent ? 'to' : 'from'} ${(isLastMsg && sendingMessageError) || errorMessages.includes(msg.getId()) ? 'bg-danger' : ''}`}
                            style={{ maxWidth: '75%', wordBreak: 'break-word' }}
                          >
                            <div
                              className="text-area"
                              dangerouslySetInnerHTML={{ __html: msg.getMessage() }}
                            />
                            <div className="text-end small mt-1">
                              {formatTime(msg.getThreadDate().date)}
                            </div>
                            {isLastMsg && isSendingNewMessage && (
                              <div>
                                <small style={{ fontSize: '10px' }}>
                                  <i className="far fa-spinner fa-spin"></i> Sending...
                                </small>
                              </div>
                            )}
                            {isLastMsg && !isSendingNewMessage && sendingMessageError && (
                              <div>
                                <small style={{ fontSize: '10px' }}>{sendingMessageError}</small>
                              </div>
                            )}
                          </div>
                          {isSent && (
                            <div className="mr-2">
                              <img
                                style={{ height: '20px' }}
                                className="pt-0 mt-0"
                                src={
                                  cndImageUrl +
                                  msg.getSmsProvider().toLowerCase().replace('.', '') +
                                  'icon.png'
                                }
                              ></img>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              );
            })}

        {isFetching && <i className="far fa-spinner fa-spin"></i>}
        {(!userSmsThreads || userSmsThreads.length === 0) && !isFetching && <i>No messages</i>}
        <div ref={messagesEndRef} />
      </div>
      {newMessageAlert && (
        <div className="text-center">
          <div className="badge badge-pill badge-success mt-2 cp p-2" onClick={scrollToBottom}>
            New Message <i className="far fa-arrow-down ml-1"></i>
          </div>
        </div>
      )}
      {!fetchingProviders &&
        !fetchingPhoneNumbersAndExtensions &&
        selectedProvider &&
        individualPhones.length > 0 && (
          <>
            <div className="input-group align-items-end">
              <div className="w-100">
                <div className="p-2 bg-white-system" style={{ borderRadius: '5px 5px 0px 0px' }}>
                  <>
                    {!fetchingProviders &&
                      !fetchingPhoneNumbersAndExtensions &&
                      selectedProvider && (
                        <>
                          <div className="dropdown d-inline-block mr-2">
                            <div
                              className="badge badge-pill info-badge-secondary cp dropdown-toggle"
                              data-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <img
                                style={{ height: '10px' }}
                                className="pt-0 mt-0"
                                alt={selectedProvider['name']}
                                src={cndImageUrl + selectedProvider['reference'] + 'icon.png'}
                              ></img>{' '}
                              {selectedProvider.name}
                            </div>
                            {availableProviders.length > 1 && (
                              <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
                                <div className="dropdown-item font-weight-bold">SMS Provider</div>
                                {availableProviders.map((item, index) => {
                                  return (
                                    <div
                                      className="dropdown-item"
                                      onClick={() => handleProviderSelect(index)}
                                    >
                                      <img
                                        style={{ height: '15px' }}
                                        className="pt-0 mt-0 mr-2"
                                        alt={selectedProvider['name']}
                                        src={cndImageUrl + item.reference + 'icon.png'}
                                      ></img>
                                      {item.name}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                    {!fetchingProviders &&
                      !fetchingPhoneNumbersAndExtensions &&
                      selectedProvider &&
                      availableSendingNumbers.length > 1 && (
                        <>
                          <div className="dropdown d-inline-block mr-2">
                            <div
                              className="badge badge-pill info-badge-secondary cp dropdown-toggle"
                              data-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i className="far fa-phone mr-1"></i>
                              {selectedSendingPhoneNumber}
                            </div>
                            <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
                              <div className="dropdown-item font-weight-bold">Messaging Line</div>
                              {availableSendingNumbers.map((number) => {
                                return (
                                  <div
                                    className="dropdown-item"
                                    onClick={() =>
                                      setSelectedSendingPhoneNumber(number.getPhoneNumber())
                                    }
                                  >
                                    <i className="far fa-phone mr-2"></i>
                                    {number.getPhoneNumber()}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}

                    {!fetchingProviders &&
                      !fetchingPhoneNumbersAndExtensions &&
                      selectedProvider &&
                      availableSendingExtensions.length > 1 && (
                        <>
                          <div className="dropdown d-inline-block mr-2">
                            <div
                              className="badge badge-pill info-badge-secondary cp dropdown-toggle"
                              data-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i className="far fa-hashtag mr-1"></i>
                              {selectedSendingExtension}
                            </div>
                            <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
                              <div className="dropdown-item font-weight-bold">
                                Messaging Extension
                              </div>
                              {availableSendingExtensions.map((extension) => {
                                return (
                                  <div
                                    className="dropdown-item"
                                    onClick={() =>
                                      setSelectedSendingExtension(extension.getExtensionId())
                                    }
                                  >
                                    <i className="far fa-hashtag mr-2"></i>
                                    {extension.getExtensionNumber()}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </>
                      )}

                    <div className="mr-3 ml-1 d-inline-block">|</div>

                    {!fetchingProviders &&
                      !fetchingPhoneNumbersAndExtensions &&
                      selectedProvider &&
                      !needsToAddPhoneNumberFromSettings &&
                      !needsToAddPhoneNumberHere && (
                        <>
                          <div className="dropdown d-inline-block mr-2">
                            <div
                              className="badge badge-pill info-badge-success cp dropdown-toggle"
                              data-toggle="dropdown"
                              aria-expanded="false"
                            >
                              <i className="far fa-phone-plus mr-1"></i>
                              {sendingTo}
                            </div>
                            {individualPhones.length > 0 && (
                              <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
                                <div className="dropdown-item font-weight-bold">Ind Numbers</div>
                                {individualPhones.map((phone) => {
                                  return (
                                    <div
                                      className="dropdown-item"
                                      onClick={() => setSendingTo(phone.getIndPhoneNumber())}
                                    >
                                      <i className="far fa-phone-plus mr-2"></i>
                                      {phone.getIndPhoneType()} - {phone.getIndPhoneNumber()}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </>
                      )}

                    <div className="mr-3 ml-1 d-inline-block">|</div>

                    <div className="dropdown d-inline-block mr-2">
                      <div
                        className="badge badge-pill info-badge-info cp dropdown-toggle"
                        data-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i className="far fa-stream mr-1"></i>
                        {templateName.substr(0, 10)}
                      </div>
                      <div className="dropdown-menu" style={{ fontSize: '9pt' }}>
                        {userTemplates !== undefined &&
                          userTemplates.map((template, templateIndex) => {
                            return (
                              <div
                                className="dropdown-item"
                                onClick={() => handleTemplateSelected(templateIndex)}
                              >
                                <i className="far fa-stream mr-2"></i>
                                {template.getLetterTitle()}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </>
                </div>
                <RichTextEditorLight
                  disabled={false}
                  onChange={handleSmsChange}
                  value={message}
                  watchValue={true}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSmsSend(
                        selectedProvider.reference,
                        selectedProvider.entityType,
                        sendingTo,
                        selectedSendingPhoneNumber,
                        messageLive,
                        selectedProvider.account_id,
                        selectedSendingExtension
                      );
                    }
                  }}
                  styles={{ borderRadius: '0px 0px 5px 5px' }}
                  classNames={['form-control', 'border-white-system']}
                  autoFocus={true}
                />
              </div>
            </div>
            <button
              className="btn btn-sm btn-primary mt-1"
              disabled={!isValidToSend()}
              onClick={() =>
                handleSmsSend(
                  selectedProvider.reference,
                  selectedProvider.entityType,
                  sendingTo,
                  selectedSendingPhoneNumber,
                  messageLive,
                  selectedProvider.account_id,
                  selectedSendingExtension
                )
              }
            >
              Send
            </button>
          </>
        )}
      {!fetchingProviders && !fetchingPhoneNumbersAndExtensions && selectedProvider && (
        <>
          {individualPhones.length === 0 && (
            <div className="mt-2">
              <i className="far fa-exclamation-circle text-danger mr-1"></i>No phone on profile{' '}
            </div>
          )}

          {needsToAddPhoneNumberFromSettings && (
            <div className="alert alert-sm alert-warning p-1 mt-2" style={{ fontSize: '9pt' }}>
              {' '}
              You need to save your phone numbers and extensions to be able to send SMS. Please go{' '}
              <a href="https://www.medicareproapp.com/agent/settings/index/voip">here</a> to do
              that.
            </div>
          )}

          {needsToAddPhoneNumberHere && (
            <>
              <div>
                <label className="label mt-2">Please type your VoIP number:</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={selectedSendingPhoneNumber}
                  onChange={handleNewPhoneNumberChange}
                />

                {!newPhoneNumberIsSaved && newPhoneNumberIsSaving && (
                  <div className="badge badge-success mt-2 cp">
                    <i className="far fa-spinner fa-spin"></i>
                  </div>
                )}
                {!newPhoneNumberIsSaved && !newPhoneNumberIsSaving && (
                  <div className="badge badge-success mt-2 cp" onClick={handlePhoneNumberSave}>
                    Save
                  </div>
                )}
                {errorSavingNewPhone && (
                  <div className="alert alert-warning p2 mt-2" style={{ fontSize: '9pt' }}>
                    <i className="far fa-exclamation-circle mr-1"></i>There was an error saving your
                    phone number, please make sure only numeric values are used.
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
      {!fetchingProviders && !fetchingPhoneNumbersAndExtensions && !selectedProvider && (
        <div className="alert alert-sm alert-info p-2 mt-2" style={{ fontSize: '9pt' }}>
          {' '}
          You can send SMS through the CRM with a connected VoIP phone or SMS service. Please go{' '}
          <a href="https://www.medicareproapp.com/agent/settings/index/voip">here</a> to do that.
          <br />
          <br />
          <b>
            SMS is currently supported with the following VoIP services: Phone.com, Dialpad,
            RingCentral, CurrentClient, Simplii, Intulse, and OpenPhone
          </b>
        </div>
      )}
    </>
  );
};
